/**
 * Express sign-up asks one question per screen, in `EXPRESS_STEP_ORDER`. Steps before Save your text
 * run before the account exists, so what they collect waits on the device (expressDraft.ts).
 * /express/$code runs the order up to Save your text; /express/send runs the send steps for the
 * selected campaign. The chrome names the stage a step belongs to, never a step count.
 */
export const EXPRESS_STAGES = ['Your text', 'Your account', 'Who gets it', 'When', 'Your committee', 'Pay'] as const;
export type ExpressStage = (typeof EXPRESS_STAGES)[number];

export const EXPRESS_STEPS = {
  message: {
    stage: 'Your text',
    label: 'Write your text',
    title: 'What do you want to say?',
    sub: 'One message, sent to everyone on your list.',
  },
  race: {
    stage: 'Your text',
    label: 'What you’re running for',
    title: 'What are you running for?',
    sub: 'We’ll use it to find your voters.',
  },
  account: {
    stage: 'Your account',
    label: 'Save your text',
    title: 'Save your text.',
    sub: 'Create your account so we can keep it. It takes a minute.',
  },
  recipients: { stage: 'Who gets it', label: 'Who gets it', title: 'Who should get it?', sub: null },
  schedule: { stage: 'When', label: 'When it goes out', title: 'When should it go out?', sub: null },
  registration: {
    stage: 'Your committee',
    label: 'About your committee',
    title: 'Tell us about your committee.',
    sub: 'Carriers need this once before political texts go out. We do the rest.',
  },
  review: {
    stage: 'Pay',
    label: 'Review, sign and pay',
    title: 'Ready when you are.',
    sub: 'Pay once. We’ll email you at each step.',
  },
} as const satisfies Record<string, { stage: ExpressStage; label: string; title: string; sub: string | null }>;

export type ExpressStepKey = keyof typeof EXPRESS_STEPS;
/** Steps only sign-up asks, before the account exists; /express/send never shows them. */
const SIGNUP_ONLY = ['race', 'account'] as const satisfies readonly ExpressStepKey[];
export type SendStepKey = Exclude<ExpressStepKey, (typeof SIGNUP_ONLY)[number]>;

export const EXPRESS_STEP_ORDER = [
  'message',
  'race',
  'account',
  'recipients',
  'schedule',
  'registration',
  'review',
] as const satisfies readonly ExpressStepKey[];

const accountIndex = EXPRESS_STEP_ORDER.indexOf('account');

export type SignupStepKey = Exclude<ExpressStepKey, SendStepKey> | 'message';
const SIGNUP_STEPS = EXPRESS_STEP_ORDER.slice(0, accountIndex + 1) as readonly SignupStepKey[];

/**
 * What the Express link runs: every step up to and including Save your text. Signed in, Save your text only
 * makes the campaign, so it asks the race there and the race has no screen of its own.
 */
export function signupStepsFor(signedIn: boolean): readonly SignupStepKey[] {
  return signedIn ? SIGNUP_STEPS.filter((step) => step !== 'race') : SIGNUP_STEPS;
}

/** Where the flow continues for the new campaign once the account exists. */
export const FIRST_STEP_AFTER_SIGNUP = EXPRESS_STEP_ORDER[accountIndex + 1] as SendStepKey;

/** What /express/send runs: every send step, in order. */
export const SEND_STEP_ORDER = EXPRESS_STEP_ORDER.filter(
  (step): step is SendStepKey => !(SIGNUP_ONLY as readonly ExpressStepKey[]).includes(step),
);
